import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Crown, Download, DollarSign, TrendingUp, RefreshCw } from "lucide-react";
import { businessTypeLabel } from "@/constants/verticals";

export default function SoldCompaniesLog() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies/sales-log");
      setItems(data.items || []);
      setTotals(data.totals_by_currency || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportExcel = async () => {
    if (!items.length) return toast.error("لا توجد بيانات للتصدير");
    try {
      const XLSX = await import("xlsx");
      const rows = items.map(c => ({
        "اسم الشركة": c.name,
        "نوع النشاط": businessTypeLabel(c.business_type) || c.business_type || "",
        "تاريخ البيع": c.sold_at ? new Date(c.sold_at).toLocaleString("ar-EG") : "",
        "سعر البيع": Number(c.sold_price) || 0,
        "العملة": c.sold_currency || "",
        "المشتري": c.sold_buyer || "",
        "الهاتف": c.phone || "",
        "ملاحظات": c.sold_notes || "",
      }));
      const totalsRows = totals.map(t => ({
        "اسم الشركة": "— الإجمالي —",
        "نوع النشاط": "",
        "تاريخ البيع": "",
        "سعر البيع": Number(t.total) || 0,
        "العملة": t.currency,
        "المشتري": "",
        "الهاتف": "",
        "ملاحظات": `إجمالي ${items.filter(i=>i.sold_currency===t.currency).length} شركة بعملة ${t.currency}`,
      }));
      const ws = XLSX.utils.json_to_sheet([...rows, {}, ...totalsRows]);
      ws["!cols"] = [{wch:32},{wch:22},{wch:22},{wch:14},{wch:8},{wch:22},{wch:16},{wch:30}];
      ws["!rtl"] = true;
      const wb = XLSX.utils.book_new();
      wb.Workbook = { Views: [{ RTL: true }] };
      XLSX.utils.book_append_sheet(wb, ws, "سجل مبيعات الشركات");
      XLSX.writeFile(wb, `sold_companies_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`تم تصدير ${items.length} شركة`);
    } catch (e) { toast.error("فشل التصدير: " + (e?.message || e)); }
  };

  return (
    <div className="space-y-6" data-testid="sold-log-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-600" /> سجل مبيعات الشركات
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            جميع الشركات التي بعتها كملكية دائمة — {items.length} شركة
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" disabled={loading} className="gap-1" data-testid="sold-log-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> تحديث
          </Button>
          <Button onClick={exportExcel} disabled={!items.length} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="sold-log-export">
            <Download className="w-4 h-4" /> تصدير Excel
          </Button>
        </div>
      </div>

      {/* Totals per currency */}
      {totals.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {totals.map(t => (
            <Card key={t.currency} className="p-4 border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
              <div className="flex items-center gap-2 text-amber-800 text-xs font-bold uppercase tracking-wider">
                <TrendingUp className="w-4 h-4" /> إجمالي {t.currency}
              </div>
              <div className="text-3xl font-bold text-amber-900 mt-2 font-mono tabular-nums">
                {fmtMoney ? fmtMoney(t.total, t.currency) : t.total.toLocaleString("ar-EG")}
              </div>
              <div className="text-xs text-amber-700 mt-1">
                من {items.filter(i => i.sold_currency === t.currency).length} صفقة
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-start">الشركة</TableHead>
              <TableHead className="text-start">نوع النشاط</TableHead>
              <TableHead className="text-start">تاريخ البيع</TableHead>
              <TableHead className="text-start">السعر</TableHead>
              <TableHead className="text-start">المشتري</TableHead>
              <TableHead className="text-start">ملاحظات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">جارٍ التحميل...</TableCell></TableRow>}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  <Crown className="w-12 h-12 mx-auto opacity-30 mb-2" />
                  لم تبع أي شركة بعد. اذهب إلى صفحة "الشركات" واضغط زر "بيع" لبدء تسجيل صفقة ملكية دائمة.
                </TableCell>
              </TableRow>
            )}
            {items.map(c => (
              <TableRow key={c.id} data-testid={`sold-row-${c.id}`}>
                <TableCell className="font-semibold">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-500" /> {c.name}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{businessTypeLabel(c.business_type) || "—"}</TableCell>
                <TableCell className="text-xs">{c.sold_at ? new Date(c.sold_at).toLocaleString("ar-EG") : "—"}</TableCell>
                <TableCell className="font-mono tabular-nums font-bold text-emerald-700">
                  <DollarSign className="w-3.5 h-3.5 inline text-amber-500" /> {Number(c.sold_price).toLocaleString("ar-EG")} <span className="text-xs text-muted-foreground">{c.sold_currency}</span>
                </TableCell>
                <TableCell>{c.sold_buyer || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={c.sold_notes}>{c.sold_notes || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
